import { omit, pathOr, sum, values } from 'ramda';
import * as R from 'remeda';
import {
  ICost as ICostInitial,
  ICost_Target,
} from '@bridebook/models/source/models/Countries/Costs.types';
import { ICost } from '@bridebook/models/source/models/Weddings/Costs.types';
import { ISupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import type { Slug } from '@bridebook/toolbox/src/types';
import { excludedSupplierCategories } from '@bridebook/web/lib/supplier/excluded-supplier-categories';

export interface IBudgetInitial {
  estimate: number;
  day: 'fri-sun' | 'sat' | 'midweek';
  season: 'off-season' | 'season';
  guests: number;
  excludeCategories: string[];
  split?: boolean;
}

interface ICostsWithInitial {
  costsInitial: Record<string, ICostInitial>;
  init: IBudgetInitial;
  shortlist: Record<string, ISupplier>;
  isGroomOnly: boolean;
  isBrideOnly: boolean;
}

/**
 * Check whether x is within range of min, max.
 */
const inRange = (x: number, min: number, max = Infinity) => x >= min && x <= max;

/**
 * Get insurance based on estimate.
 */
const getInsurance = (estimate: number) => {
  let insurance = 0;
  switch (true) {
    case estimate <= 5000:
      insurance = 0;
      break;
    case estimate <= 10000:
      insurance = 60;
      break;
    case estimate <= 20000:
      insurance = 100;
      break;
    case estimate <= 30000:
      insurance = 150;
      break;
    case estimate <= 50000:
      insurance = 220;
      break;
    default:
      insurance = 300;
  }
  return insurance;
};

/**
 * Get value to scale based on number of guests.
 */
const getGuestsWeight = (guests: number) => {
  let perc = 0;
  switch (true) {
    case guests < 50:
      perc = 0.5;
      break;
    case guests < 100:
      perc = 1;
      break;
    case guests < 150:
      perc = 1.33;
      break;
    case guests < 250:
      perc = 1.66;
      break;
    default:
      perc = 2;
  }
  return perc;
};

/**
 * Accepts targets and user estimate, returns relative percentage.
 */
const getTargetRelative = (targets: Array<ICost_Target>, estimate: number) =>
  targets.filter((target) => inRange(estimate, target.range[0], target.range[1]))[0].relative;

/**
 * Get cost percentage from targets.
 * Apply percentage modifiers for selected supplier categories.
 */
const getPercentage = (
  targets: Array<ICost_Target>,
  init: IBudgetInitial,
  supplierType?: string,
) => {
  const { guests, excludeCategories, estimate } = init;
  if (supplierType && excludeCategories.includes(supplierType)) return 0;
  const guestsWeight = getGuestsWeight(guests);
  let percentage = getTargetRelative(targets, estimate) || 0;
  switch (true) {
    case supplierType === 'catering':
      percentage *= 0.5 + 0.5 * guestsWeight;
      break;
    case supplierType === 'stationery':
      percentage *= 0.25 + 0.75 * guestsWeight;
      break;
    case supplierType === 'music' && excludeCategories.includes('liveband'):
      percentage *= 0.35;
      break;
  }
  return percentage;
};

/**
 * getBudgetEstimate - subtracts insurance and other fixed costs from initial estimate
 */
const getBudgetEstimate = (init: IBudgetInitial, absoluteFees = 0): number => {
  const { excludeCategories, estimate } = init;
  const insurance = !excludeCategories.includes('Insurance') ? getInsurance(estimate) : 0;
  return estimate - insurance - absoluteFees;
};

const fixRoundingErrors = (
  estCosts: Array<Partial<ICost>>,
  costs: Array<Partial<ICostInitial>>,
  estimate: number,
) => {
  const sumAll = sum(R.map(estCosts, pathOr(0, ['estimate'])));
  const diff = estimate - sumAll;
  if (diff) {
    const index = costs.findIndex((cost) => cost.supplierType === 'venue');
    estCosts[index].estimate! += diff;
  }
  return estCosts;
};

/**
 * Calculate and add "estimate" prop based on "percentage". Omit "percentage" prop after.
 */
const getCostsWithEstimates = (
  costs: Array<Partial<ICostInitial & ICost>>,
  init: IBudgetInitial,
) => {
  const absoluteFees = sum(R.map(costs, pathOr(0, ['absolute'])));
  const budgetEstimate = getBudgetEstimate(init, absoluteFees);
  const { estimate, excludeCategories } = init;

  const estCosts = costs.map((cost) => {
    cost.estimate = cost.absolute || Math.round(budgetEstimate * pathOr(0, ['percentage'], cost));
    if (cost.name === 'Insurance' && !excludeCategories.includes('Insurance')) {
      cost.estimate = getInsurance(estimate);
    }
    return omit(['absolute', 'name'], cost);
  });

  return fixRoundingErrors(estCosts, costs, estimate);
};

/**
 * Normalize so sum of percentages is 100
 */
const getCostsNormalized = (costs: Array<Partial<ICost>>) => {
  const sumPerc = sum(R.map(costs, pathOr(0, ['percentage'])));
  const diff = 1 - sumPerc;

  return costs.map((cost) => {
    if (cost.percentage) cost.percentage /= 1 - diff;
    return cost;
  });
};

/**
 * Process "targets" and omit before saving to redux state.
 */
const getCostsWithPercentages = ({
  costsInitial,
  init,
  isBrideOnly,
  isGroomOnly,
}: {
  costsInitial: Record<string, ICostInitial>;
  init: IBudgetInitial;
  isGroomOnly: boolean;
  isBrideOnly: boolean;
}) => {
  const excluded: (Slug | '')[] = isGroomOnly
    ? excludedSupplierCategories.groomOnly
    : isBrideOnly
      ? excludedSupplierCategories.brideOnly
      : [];

  return Object.values(costsInitial)
    .filter(
      (cost) =>
        !(
          // checking for cost.name because some categories don't have supplierType (Insurance)
          (
            init.excludeCategories.includes(cost.name || '') ||
            init.excludeCategories.includes(cost.supplierType || '') ||
            excluded.includes(cost.supplierType || '')
          )
        ),
    )
    .map((cost) => {
      const weddingCosts: Partial<ICost> = { ...cost, actual: 0, estimate: 0 };
      const { supplierType, name } = cost;

      const targets = pathOr(null, ['targets', init.season, init.day], cost);
      if (targets) {
        weddingCosts.percentage = getPercentage(targets, init, supplierType) || 0;
      }
      if (name === 'Favours and Gifts') {
        weddingCosts.percentage = cost.relative;
      }
      return omit(['targets', 'active', 'articles', 'relative'], weddingCosts);
    });
};

/**
 * Read booked suppliers from shortlist and set actual.
 */
const getCostsWithActual = (
  costsWithEstimates: Array<Partial<ICost>>,
  shortlist: Record<string, ISupplier>,
) =>
  costsWithEstimates.map((cost) => {
    // To prevent comparing with `undefined`, return early if the cost doesn't
    // have a `supplierType`
    if (!cost.supplierType) {
      return cost;
    }

    const shortlistedSuppliers = values(shortlist) || [];
    // For calculating the total, it only needs the booked suppliers with the
    // same type as the cost
    const booked = shortlistedSuppliers.filter(
      (shortlistedSupplier) =>
        shortlistedSupplier.type === cost.supplierType && shortlistedSupplier.booked === true,
    );

    if (booked.length) {
      const bookedTotal = sum(booked.map((bookedSupplier) => bookedSupplier.price || 0));
      if (bookedTotal !== 0) {
        cost.actual = bookedTotal;
      }
    }
    return cost;
  });

/**
 * 1. Get percentages for every cost based on user input budget initial.
 * 2. Normalize percentages to make sure total is 100%.
 * 3. Calculate every cost estimate based on percentage and initial budget target.
 * Returns costs with calculated estimates without "targets" and "percentage" props.
 */
export const processCosts = ({
  costsInitial,
  init,
  shortlist,
  isGroomOnly,
  isBrideOnly,
}: ICostsWithInitial) => {
  const costsWithPercentages = getCostsWithPercentages({
    costsInitial,
    init,
    isGroomOnly,
    isBrideOnly,
  });
  const costsNormalized = getCostsNormalized(costsWithPercentages);
  const costsWithEstimates = getCostsWithEstimates(costsNormalized, init);
  const costsWithActual = getCostsWithActual(costsWithEstimates, shortlist);
  return costsWithActual;
};

export const getSplitCosts = async ({
  costsInitial,
  init,
  shortlist,
  isGroomOnly,
  isBrideOnly,
}: {
  costsInitial: Record<string, ICostInitial>;
  init: IBudgetInitial;
  shortlist: Record<string, ISupplier>;
  isGroomOnly: boolean;
  isBrideOnly: boolean;
}) => processCosts({ costsInitial, init, shortlist, isGroomOnly, isBrideOnly });
